import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import type { Except } from "type-fest";
import { z } from "zod";

import {
    orderChangedEventZodSchema,
    type OrderChangedEvent,
} from "@@/shared/orders/order-events/events/order-changed-event/order-changed.event";
import {
    isOrderCreatedEvent,
    orderCreatedEventZodSchema,
    type OrderCreatedEvent,
} from "@@/shared/orders/order-events/events/order-created.event";
import {
    orderPayRequestFulfilledEventZodSchema,
    type OrderPayRequestFulfilledEvent,
} from "@@/shared/orders/order-events/events/order-pay-request-events/order-pay-request-fulfilled.event";
import {
    orderPaymentFulfilledEventZodSchema,
    type OrderPaymentFulfilledEvent,
} from "@@/shared/orders/order-events/events/order-payment-events/order-payment-fulfilled.event";
import {
    isPrice,
    legacyCustomerServiceFeePrice,
    priceFactory,
    priceZodSchema,
    type Price,
} from "@@/shared/prices/price";

import { assertNever } from "@@/shared/assert-never";
import { orderIdZodSchema, type OrderId } from "@@/shared/orders/order-id";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { repeat } from "@@/utilities/repeat";
import { unique } from "@@/utilities/unique";
import { parseFulfillmentDate } from "../parse-fulfillment-date";
import {
    settlementIdFactory,
    settlementIdZodSchema,
    type SettlementId,
} from "../settlement-id";
import {
    settlementPaymentTypeZodEnum,
    type SettlementPaymentType,
} from "./settlement-payment-type";
import { getOriginPrice } from "./shared/get-origin-price";

type PaymentSettlement = {
    readonly _id: SettlementId;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "SETTLEMENT";
    readonly type: "PAYMENT";
    readonly paymentType: SettlementPaymentType;
    readonly isPayRequest: boolean;
    readonly settlementEvent:
        | OrderPayRequestFulfilledEvent
        | OrderPaymentFulfilledEvent;
    readonly price: Price;
    readonly orderId: OrderId;
    readonly orderConfirmedFulfillmentDate?: IsoAndUnixTimestamp;
    readonly originEventFulfillmentDate: IsoAndUnixTimestamp;
    readonly originEvent: OrderCreatedEvent | OrderChangedEvent;
};

const paymentSettlementZodObject = z.object({
    _id: settlementIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("SETTLEMENT"),
    type: z.literal("PAYMENT"),
    orderId: orderIdZodSchema,
    paymentType: settlementPaymentTypeZodEnum,
    isPayRequest: z.boolean(),
    originEvent: z.union([
        orderCreatedEventZodSchema,
        orderChangedEventZodSchema,
    ]),
    settlementEvent: z.union([
        orderPayRequestFulfilledEventZodSchema,
        orderPaymentFulfilledEventZodSchema,
    ]),
    price: priceZodSchema,
    orderConfirmedFulfillmentDate: isoAndUnixZodSchema.optional(),
    originEventFulfillmentDate: isoAndUnixZodSchema,
});

const paymentSettlementZodSchema: z.ZodSchema<PaymentSettlement> =
    paymentSettlementZodObject;

const _getFullPrice = (
    settlementEvent: PaymentSettlement["settlementEvent"],
) => {
    switch (settlementEvent.type) {
        case "PAYMENT_FULFILLED":
            // CHECKING IF PRICE BECAUSE OF BAD LOCAL DATA
            return isPrice(settlementEvent.paidAmount)
                ? settlementEvent.paidAmount
                : priceFactory({
                      amountIncludingVat:
                          settlementEvent.paidAmount as unknown as number,
                  });
        case "PAY_REQUEST_FULFILLED":
            return settlementEvent.price;
        default:
            assertNever(settlementEvent, "Unknown settlement event type", {
                valueProp: "type",
            });
    }
};

const paymentSettlementFactory = (
    params: Except<
        PaymentSettlement,
        | "_id"
        | "_createdAt"
        | "_type"
        | "type"
        | "price"
        | "orderConfirmedFulfillmentDate"
        | "originEventFulfillmentDate"
        | "orderId"
        | "originEvent"
    > & {
        originEvents: Array<OrderCreatedEvent | OrderChangedEvent>;
        orderConfirmedFulfillmentDate: IsoAndUnixTimestamp | undefined;
    },
): PaymentSettlement[] => {
    const settlementEvent = params.settlementEvent;
    const fullPrice = _getFullPrice(settlementEvent);

    // Create a settlement for each origin event
    const settlements = params.originEvents.map(
        (originEvent: PaymentSettlement["originEvent"]): PaymentSettlement => {
            const price = getOriginPrice(originEvent);

            return {
                _id: settlementIdFactory(),
                _createdAt: nowIsoAndUnix(),
                _type: "SETTLEMENT",
                type: "PAYMENT",
                orderId: originEvent.orderId,
                price,
                originEventFulfillmentDate: parseFulfillmentDate(originEvent),
                originEvent,
                paymentType: params.paymentType,
                settlementEvent: params.settlementEvent,
                isPayRequest: settlementEvent.type === "PAY_REQUEST_FULFILLED",
                orderConfirmedFulfillmentDate:
                    params.orderConfirmedFulfillmentDate,
            };
        },
    );

    // Make sure the total price of all settlements is the
    // same as the full price
    const originCreatedEventIdsWhereOrderGroupHasLegacyServiceFee = unique(
        params.originEvents
            .filter(isOrderCreatedEvent)
            .filter(item => item.orderGroupHasLegacyServiceFee)
            .map(item => item._id),
    );
    const calculatedFullPrice = sumPrices([
        ...settlements.map(settlement => settlement.price),
        ...(originCreatedEventIdsWhereOrderGroupHasLegacyServiceFee
            ? // create one legacy service fee price for each origin created event with an order group that has a legacy service fee
              repeat(
                  legacyCustomerServiceFeePrice,
                  originCreatedEventIdsWhereOrderGroupHasLegacyServiceFee.length,
              )
            : []),
    ]);
    if (
        fullPrice.amountIncludingVat !== calculatedFullPrice.amountIncludingVat
    ) {
        throw new Error(
            `Total price of settlements does not match full price; ${fullPrice.amountIncludingVat} !== ${calculatedFullPrice.amountIncludingVat}, order: ${params.settlementEvent.orderId}`,
        );
    }

    return settlements;
};

export {
    paymentSettlementFactory,
    paymentSettlementZodObject,
    paymentSettlementZodSchema,
};
export type { PaymentSettlement, SettlementPaymentType };
